/**
 * SEO Mappings Constants
 * Maps WooRank API values to React component-ready values
 */

/**
 * WooRank status to React status mapping
 */
export const STATUS_MAP = {
  good: 'success',
  average: 'warning',
  bad: 'error',
  neutral: 'info',
};

/**
 * WooRank status to icon type mapping
 */
export const ICON_TYPE_MAP = {
  good: 'success',
  average: 'question',
  bad: 'error',
  neutral: 'info',
};

/**
 * Importance level configuration
 * Maps WooRank importance values (1-3) to display properties
 */
export const IMPORTANCE_CONFIG = {
  1: { level: 1, text: 'Low Impact' },
  2: { level: 2, text: 'Medium Impact' },
  3: { level: 3, text: 'High Impact' },
};

/**
 * Solvability level configuration
 * Maps WooRank solvability values (1-3) to display properties
 */
export const SOLVABILITY_CONFIG = {
  1: { level: 1, text: 'Easy to Solve' },
  2: { level: 2, text: 'Hard to Solve' },
  3: { level: 3, text: 'Very Hard to Solve' },
};

/**
 * Default values when data is missing
 */
export const DEFAULTS = {
  importance: { level: 0, text: '' },
  solvability: { level: 0, text: '' },
  status: 'error',
  iconType: 'error',
};

/**
 * Title tag length thresholds
 * Characters: 20-70, Pixels: 200-569
 */
export const TITLE_THRESHOLDS = {
  MIN_LENGTH: 20,
  MAX_LENGTH: 70,
  MIN_PIXELS: 200,
  MAX_PIXELS: 569,
};

/**
 * Meta description length thresholds
 * Characters: 70-160, Pixels: 600-940
 */
export const DESCRIPTION_THRESHOLDS = {
  MIN_LENGTH: 70,
  MAX_LENGTH: 160,
  MIN_PIXELS: 600,
  MAX_PIXELS: 940,
};

/**
 * Helper function to get React status from WooRank status
 * @param {string} wooRankStatus - The status from WooRank API
 * @returns {string} - The React component status
 */
export const getStatus = (wooRankStatus) => {
  return STATUS_MAP[wooRankStatus] || DEFAULTS.status;
};

/**
 * Helper function to get icon type from WooRank status
 * @param {string} wooRankStatus - The status from WooRank API
 * @returns {string} - The icon type for the component
 */
export const getIconType = (wooRankStatus) => {
  return ICON_TYPE_MAP[wooRankStatus] || DEFAULTS.iconType;
};

/**
 * Helper function to get importance config from WooRank importance value
 * @param {number} importance - The importance value from WooRank API (1-3)
 * @returns {Object} - The importance configuration { level, text }
 */
export const getImportance = (importance) => {
  const value = parseInt(importance, 10);
  return IMPORTANCE_CONFIG[value] || DEFAULTS.importance;
};

/**
 * Helper function to get solvability config from WooRank solvability value
 * @param {number} solvability - The solvability value from WooRank API (1-3)
 * @returns {Object} - The solvability configuration { level, text }
 */
export const getSolvability = (solvability) => {
  const value = parseInt(solvability, 10);
  return SOLVABILITY_CONFIG[value] || DEFAULTS.solvability;
};

